import React from 'react';
import { useNavigate } from 'react-router-dom';
import './TermsOfService.css'; // reuse same styles

const LAST_UPDATED = 'April 14, 2026';

function Section({ title, children }) {
  return (
    <div className="tos__section">
      <h2 className="tos__section-title">{title}</h2>
      <div className="tos__section-body">{children}</div>
    </div>
  );
}

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <div className="page tos">
      <div className="grid-bg" aria-hidden="true" />

      <header className="tos__header">
        <button className="page-back-btn" onClick={() => navigate('/profile')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          Profile
        </button>
        <h1 className="tos__title">Privacy Policy</h1>
        <p className="tos__updated">Last updated: {LAST_UPDATED}</p>
      </header>

      <div className="tos__body">

        <Section title="1. Overview">
          <p>Hauoli &amp; Maluhia LLC ("Company," "we," "us," or "our") operates the StreamStack mobile and web application ("App"). This Privacy Policy explains how we collect, use, store, and protect your personal information when you use the App. By creating an account or using the App you agree to the practices described in this policy. If you do not agree, please do not use the App.</p>
        </Section>

        <Section title="2. Information We Collect">
          <p><strong>Account Information.</strong> When you register we collect your email address, display name, and any optional profile details you provide. If you connect a third-party sign-in provider (e.g., Google), we receive only the profile data that provider shares with us.</p>
          <p><strong>Device &amp; App Usage Data.</strong> We automatically collect information about your device (model, OS version, unique device identifiers), app session data (session start/end times, active minutes, in-app actions), and crash or performance logs. This data is used solely to calculate your Stream rewards and to improve app stability.</p>
          <p><strong>Financial Information.</strong> When you request a withdrawal we collect your PayPal email address or other payout method details. We do not store full payment-card numbers. Payout processing is handled by third-party payment providers subject to their own privacy policies.</p>
          <p><strong>Communications.</strong> If you contact our support team we retain the content of those communications to resolve your issue and improve our service.</p>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul>
            <li>To create and manage your account and authenticate your identity.</li>
            <li>To track eligible watch time and calculate Stream token rewards accurately.</li>
            <li>To process withdrawal requests and verify payout eligibility.</li>
            <li>To detect and prevent fraud, abuse, and violations of our Terms of Service.</li>
            <li>To send transactional communications (e.g., withdrawal confirmations, security alerts).</li>
            <li>To improve app performance, features, and user experience through aggregated analytics.</li>
            <li>To comply with applicable legal obligations.</li>
          </ul>
          <p>We do <strong>not</strong> sell your personal information to third parties. We do not use your data to serve third-party targeted advertisements within the App.</p>
        </Section>

        <Section title="4. Data Sharing">
          <p><strong>Service Providers.</strong> We share data with trusted vendors who process it on our behalf — including Firebase (Google LLC) for database and authentication services, RevenueCat for in-app purchase management, and payment processors for withdrawals. These vendors are contractually required to protect your data and use it only for the services we specify.</p>
          <p><strong>Legal Requirements.</strong> We may disclose your information if required by law, court order, or governmental authority, or if we believe in good faith that disclosure is necessary to protect our rights, your safety, or the safety of others.</p>
          <p><strong>Business Transfers.</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you via email or in-app notice before your information becomes subject to a different privacy policy.</p>
        </Section>

        <Section title="5. Data Retention">
          <p>We retain your account data for as long as your account is active. If you request account deletion, we will delete or anonymize your personal data within 30 days, except where we are required to retain it for legal, fraud-prevention, or financial compliance purposes (typically up to 7 years for transaction records).</p>
        </Section>

        <Section title="6. Security">
          <p>We implement industry-standard technical and organizational measures to protect your data, including encrypted data transmission (TLS), access controls limiting who can view personal data, and regular security reviews. No system is perfectly secure; in the event of a data breach we will notify affected users as required by applicable law.</p>
        </Section>

        <Section title="7. Your Rights">
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul>
            <li><strong>Access</strong> the personal data we hold about you.</li>
            <li><strong>Correct</strong> inaccurate or incomplete information.</li>
            <li><strong>Delete</strong> your account and associated personal data.</li>
            <li><strong>Port</strong> your data to another service in a machine-readable format.</li>
            <li><strong>Object</strong> to or restrict certain processing activities.</li>
          </ul>
          <p>To exercise any of these rights, email us at <strong>support@streamstack.us</strong>. We will respond within 30 days.</p>
        </Section>

        <Section title="8. Children's Privacy">
          <p>StreamStack is intended for users aged <strong>18 and older</strong>. We do not knowingly collect personal information from anyone under 18. If we learn that we have collected data from a minor, we will delete it promptly. If you believe a minor has registered, please contact us at support@streamstack.us.</p>
        </Section>

        <Section title="9. Cookies &amp; Tracking Technologies">
          <p>The web version of StreamStack uses browser local storage to persist your authentication session and theme preference. We do not deploy third-party advertising cookies. Analytics data (e.g., session length, crash reports) is collected through Firebase Analytics and is subject to Google's privacy practices.</p>
        </Section>

        <Section title="10. Third-Party Links">
          <p>The App may contain links to third-party websites or services. We are not responsible for the privacy practices of those parties and encourage you to review their privacy policies before providing any personal information.</p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting a notice in the App or sending an email to the address associated with your account at least 14 days before the changes take effect. Continued use of the App after the effective date constitutes acceptance of the updated policy.</p>
        </Section>

        <Section title="12. Contact Us">
          <p>If you have questions, concerns, or requests regarding this Privacy Policy, please contact:</p>
          <p>
            <strong>Hauoli &amp; Maluhia LLC</strong><br />
            Email: <strong>support@streamstack.us</strong><br />
            Governing law: State of Georgia, United States
          </p>
        </Section>

      </div>
    </div>
  );
}
